import { LogIn, Home, ShieldOff } from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import { Button, Card, PageHeader } from '@/components/inputs';
import { usePageTrail, usePageIcon } from '../../navigation/usePageTrail';
import type { AdminRole } from '../../types/admin';

interface UnauthorizedPageProps {
  /** Honest required tier — set only by route guards (`requireMinimumRole`)
   *  which know the exact role the navigation needed. Selects the 403 variant. */
  required?: AdminRole;
  /** Presence flag set by the axios 403 interceptor when the server denies a
   *  request and the client doesn't know the required tier. Also selects the
   *  403 variant. Kept separate from `required` so the URL never claims a
   *  role the server didn't actually demand. */
  forbidden?: boolean;
}

/**
 * The page's title is the PageHeader's h1, so the panel carries no heading of
 * its own: the page has exactly one heading naming the variant. The trail's
 * last crumb ("Access denied" / "Authentication required") comes from the
 * page trail map, which reads the same search params.
 */
export function UnauthorizedPage({ required, forbidden }: UnauthorizedPageProps = {}) {
  const router = useRouter();
  const crumbs = usePageTrail();
  const icon = usePageIcon();

  if (required || forbidden) {
    return (
      <div className="space-y-4">
        <PageHeader icon={icon} crumbs={crumbs} title="Access denied" />
        <div className="flex items-center justify-center py-16">
          <Card className="max-w-md w-full shadow-xl" contentClassName="p-8 text-center">
            <div className="size-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldOff className="size-8 text-destructive" />
            </div>

            <p className="text-4xl font-bold text-muted-foreground/50 mb-2">403</p>

            <p className="text-muted-foreground mb-6">
              You don't have permission to view this page.
            </p>

            <div className="flex justify-center">
              <Button label="Go to dashboard" preIcon={Home} onClick={() => router.navigate({ to: '/admin' })} />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader icon={icon} crumbs={crumbs} title="Authentication required" />
      <div className="flex items-center justify-center py-16">
        <Card className="max-w-md w-full shadow-xl" contentClassName="p-8 text-center">
          <div className="size-16 bg-warning-100 dark:bg-warning-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn className="size-8 text-warning-600 dark:text-warning-400" />
          </div>

          <p className="text-4xl font-bold text-muted-foreground/50 mb-2">401</p>

          <p className="text-muted-foreground mb-6">
            You need to be signed in to access this page.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button label="Sign in" preIcon={LogIn} onClick={() => router.navigate({ to: '/admin/login' })} />
            <Button variant="outline" label="Go to dashboard" preIcon={Home} onClick={() => router.navigate({ to: '/admin' })} />
          </div>
        </Card>
      </div>
    </div>
  );
}
